import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["data-testid", "aria-busy"]
const _hoisted_2 = /*#__PURE__*/_createElementVNode("h2", { class: "m-0 text-base font-bold text-textPrimary" }, "Fila concluída", -1)
const _hoisted_3 = ["data-testid"]
const _hoisted_4 = ["data-testid"]
const _hoisted_5 = { class: "flex flex-wrap items-center gap-2" }
const _hoisted_6 = /*#__PURE__*/_createElementVNode("h2", { class: "m-0 text-base font-bold text-textPrimary" }, "Ataque em sequência", -1)
const _hoisted_7 = ["data-testid"]
const _hoisted_8 = /*#__PURE__*/_createElementVNode("span", { class: "flex-1" }, null, -1)
const _hoisted_9 = ["data-testid"]
const _hoisted_10 = ["aria-valuenow", "aria-valuemin", "aria-valuemax", "aria-valuetext"]
const _hoisted_11 = ["data-testid"]
const _hoisted_12 = { class: "flex flex-wrap items-center gap-2" }
const _hoisted_13 = { class: "text-lg font-bold text-textPrimary" }
const _hoisted_14 = {
  key: 0,
  class: "font-mono text-xs text-neutral50"
}
const _hoisted_15 = { class: "flex flex-wrap items-center gap-2" }
const _hoisted_16 = /*#__PURE__*/_createElementVNode("span", { class: "flex-1" }, null, -1)
const _hoisted_17 = ["data-testid", "disabled"]
const _hoisted_18 = /*#__PURE__*/_createElementVNode("kbd", { class: "rounded border border-lightGray30 px-1 font-mono text-[11px]" }, "S", -1)
const _hoisted_19 = ["data-testid", "disabled"]
const _hoisted_20 = /*#__PURE__*/_createElementVNode("kbd", { class: "rounded border border-white/40 px-1 font-mono text-[11px]" }, "Enter", -1)
const _hoisted_21 = ["data-testid"]
const _hoisted_22 = ["data-testid"]

import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

const IGNORED_FOCUS_SELECTOR =
  'input, select, textarea, button, a[href], [contenteditable]:not([contenteditable="false"]), [role="button"]'

/**
 * O evento não é um atalho do painel: veio com modificador (Ctrl/Cmd+S é o
 * salvar do navegador), é tecla segurada, composição de IME, já foi tratado,
 * ou o foco está num elemento em que Enter/S têm significado próprio.
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'QueueAttackPanel',
  props: {
    isActive: { type: Boolean },
    isFinished: { type: Boolean },
    position: {},
    total: {},
    title: {},
    subtitle: { default: '' },
    nextLabel: {},
    doneCount: { default: 0 },
    skippedCount: { default: 0 },
    takenCount: { default: 0 },
    testId: { default: 'queue-attack-panel' },
    busy: { type: Boolean, default: false }
  },
  emits: ["done", "skip", "quit", "finish"],
  setup(__props: any, { emit }) {

const props = __props;





const progressWidth = computed(() => {
  if (props.total <= 0) return '0%'
  return `${Math.round(((props.position - 1) / props.total) * 100)}%`
})

/** Com fila vazia (`total = 0`) min/max/now colapsam em 0, sem valor fora da faixa. */
const progressAria = computed(() => {
  const min = props.total > 0 ? 1 : 0
  const max = Math.max(props.total, min)
  return { min, max, now: Math.min(Math.max(props.position, min), max) }
})

/** Elementos em que Enter/S têm significado próprio e o atalho não pode roubar. */
function isForeignKeystroke(event: KeyboardEvent): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return true
  if (event.isComposing || event.repeat || event.defaultPrevented) return true
  const target = event.target as HTMLElement | null
  return Boolean(target?.matches?.(IGNORED_FOCUS_SELECTOR))
}

/**
 * `Enter`/`S` são atalhos GLOBAIS, porque quem ataca a fila trabalha com as
 * mãos no teclado e o foco normalmente está no corpo da página. Digitar numa
 * observação ou apertar um botão nunca pode virar "tratado, próximo" — daí o
 * corte por tipo de elemento em foco, igual ao do mockup.
 */
/**
 * UX-02: um atalho durante a ação em voo continua IGNORADO (enfileirar faria um
 * Enter repetido tratar o item seguinte sem a pessoa vê-lo), mas não em
 * silêncio: o painel diz para aguardar até a ação terminar.
 */
const hasIgnoredShortcut = ref(false)
watch(
  () => props.busy,
  (busy) => {
    if (!busy) hasIgnoredShortcut.value = false
  }
)

const isShortcutKey = (event: KeyboardEvent): boolean =>
  event.key === 'Enter' || event.key?.toLowerCase() === 's'

function onKeydown(event: KeyboardEvent): void {
  if (!props.isActive || props.isFinished) return
  if (isForeignKeystroke(event)) return
  if (props.busy) {
    if (isShortcutKey(event)) {
      event.preventDefault()
      hasIgnoredShortcut.value = true
    }
    return
  }

  if (event.key === 'Enter') {
    event.preventDefault()
    emit('done')
    return
  }
  if (event.key?.toLowerCase() === 's') {
    event.preventDefault()
    emit('skip')
  }
}

onMounted(() => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))

return (_ctx: any,_cache: any) => {
  return (_ctx.isActive)
    ? (_openBlock(), _createElementBlock("section", {
        key: 0,
        class: "flex flex-col gap-3 rounded-[10px] border-2 border-primary bg-white p-4",
        "data-testid": _ctx.testId,
        "aria-busy": _ctx.busy ? 'true' : 'false'
      }, [
        (_ctx.isFinished)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _hoisted_2,
              _createElementVNode("p", {
                class: "m-0 text-sm text-neutral50",
                "data-testid": `${_ctx.testId}-summary`
              }, [
                _createTextVNode(_toDisplayString(_ctx.doneCount) + " tratados · " + _toDisplayString(_ctx.skippedCount) + " pulados", 1),
                (_ctx.takenCount > 0)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createTextVNode(" · " + _toDisplayString(_ctx.takenCount) + " com outra pessoa", 1)
                    ], 64))
                  : _createCommentVNode("", true),
                _createTextVNode(". Os pulados continuam na lista. ")
              ], 8, _hoisted_3),
              _renderSlot(_ctx.$slots, "summary"),
              _createElementVNode("div", null, [
                _createElementVNode("button", {
                  type: "button",
                  class: "rounded-md border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-white",
                  "data-testid": `${_ctx.testId}-finish`,
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('finish')))
                }, " Voltar para a lista ", 8, _hoisted_4)
              ])
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createElementVNode("div", _hoisted_5, [
                _hoisted_6,
                _createElementVNode("span", {
                  class: "text-xs text-neutral50",
                  "data-testid": `${_ctx.testId}-progress-label`
                }, _toDisplayString(_ctx.position) + " de " + _toDisplayString(_ctx.total), 9, _hoisted_7),
                _renderSlot(_ctx.$slots, "clock"),
                _hoisted_8,
                _createElementVNode("button", {
                  type: "button",
                  class: "rounded-md border border-lightGray30 px-3 py-1.5 text-sm font-medium text-textPrimary",
                  "data-testid": `${_ctx.testId}-quit`,
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('quit')))
                }, " Sair ", 8, _hoisted_9)
              ]),
              _createElementVNode("div", {
                class: "h-1.5 overflow-hidden rounded-[3px] bg-lightGray30",
                role: "progressbar",
                "aria-label": "Progresso da fila",
                "aria-valuenow": progressAria.value.now,
                "aria-valuemin": progressAria.value.min,
                "aria-valuemax": progressAria.value.max,
                "aria-valuetext": `${_ctx.position} de ${_ctx.total}`
              }, [
                _createElementVNode("i", {
                  class: "block h-full bg-primary transition-[width] duration-200 motion-reduce:transition-none",
                  style: _normalizeStyle({ width: progressWidth.value }),
                  "data-testid": `${_ctx.testId}-progress-bar`
                }, null, 12, _hoisted_11)
              ], 8, _hoisted_10),
              _createElementVNode("div", _hoisted_12, [
                _createElementVNode("span", _hoisted_13, _toDisplayString(_ctx.title), 1),
                (_ctx.subtitle)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(_ctx.subtitle), 1))
                  : _createCommentVNode("", true),
                _renderSlot(_ctx.$slots, "badges")
              ]),
              _renderSlot(_ctx.$slots, "facts"),
              _renderSlot(_ctx.$slots, "message"),
              _createElementVNode("div", _hoisted_15, [
                _renderSlot(_ctx.$slots, "actions"),
                _hoisted_16,
                _createElementVNode("button", {
                  type: "button",
                  class: "rounded-md border border-lightGray30 px-3 py-1.5 text-sm font-medium text-textPrimary disabled:cursor-not-allowed disabled:opacity-50",
                  "data-testid": `${_ctx.testId}-skip`,
                  disabled: _ctx.busy,
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('skip')))
                }, [
                  _createTextVNode(" Pular "),
                  _hoisted_18
                ], 8, _hoisted_17),
                _createElementVNode("button", {
                  type: "button",
                  class: "rounded-md border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50",
                  "data-testid": `${_ctx.testId}-done`,
                  disabled: _ctx.busy,
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (emit('done')))
                }, [
                  _createTextVNode(" Tratado, próximo "),
                  _hoisted_20
                ], 8, _hoisted_19)
              ]),
              (_ctx.busy && hasIgnoredShortcut.value)
                ? (_openBlock(), _createElementBlock("p", {
                    key: 0,
                    class: "m-0 text-xs text-neutral50",
                    role: "status",
                    "data-testid": `${_ctx.testId}-busy`
                  }, " Aguarde: o plantão ainda está sendo aberto. Tente de novo em instantes. ", 8, _hoisted_21))
                : _createCommentVNode("", true),
              _createElementVNode("p", {
                class: "m-0 text-xs text-neutral50",
                "data-testid": `${_ctx.testId}-next`
              }, _toDisplayString(_ctx.nextLabel ? `A seguir: ${_ctx.nextLabel}` : 'Este é o último da fila.'), 9, _hoisted_22)
            ], 64))
      ], 8, _hoisted_1))
    : _createCommentVNode("", true)
}
}

})